import React, { useState, useEffect, useLayoutEffect, useMemo } from 'react';
import { DownOutlined } from '@ant-design/icons';
import NP from 'number-precision';
import { observer } from 'mobx-react';
import Cookies from 'js-cookie';
import { useFavicon, useTitle } from 'ahooks';
import { Message, Icon, TabLink } from '@/components';
import { ProLayout } from '@ant-design/pro-components';
import { useLocation, Outlet, useNavigate } from 'react-router-dom';
import defaultSettings from '@/const/defaultSettings';
import { AccountStore, CommonStore } from '@/stores';
import HeaderMenu from './HeaderMenu';
import SiderMenu from './SiderMenu';
import TabMenu from './TabMenu';
import Breadcrumb from './Breadcrumb';
import HeaderLeftContent from './HeaderLeftContent';
import { getFlatMenus, formartMenu } from '@/tools/getFlatMenus';
import { ApplicationInfo } from '@/const/application';
import getMatchMenu from '@/tools/getMatchMenu';
import AvatarPopover from './AvatarPopover';
import type { UserInfo } from '@/stores/account';
import type { MenuDataItem } from '@/const/route.config';
import { PLATFORM_TYPES, RELATION_TYPES } from '@/const/csc';
import { getApplicationUrl, TokenKey } from '@/const/serviceEnv';
import { Badge, Space, Tooltip } from 'antd';
import theme, { setTheme } from '@/theme';
import WujieReact from 'wujie-react';
import NoticeDrawer from './NoticeDrawer';
import AuthContainer from './AuthContainer';

import './index.less';

export interface AuthorizedModuleType {
  authorizedCount: number;
  moduleId: string | number;
  moduleName: string;
  status: 0 | 1;
  totalGrantNum: number;
}
type BandgeFunType = 'approve' | 'message';

const loginUrl = '/account/login';
const HasToken = Cookies.get('token');
// 纯净模式展示的路由
const pureRoute = new Map([
  ['/404', true],
  ['/noAuth', true],
  ['/redirectPage', true],
]);

const { systemId, title } = ApplicationInfo;
const { bus } = WujieReact;

const BasicLayout: React.FC = () => {
  const { csShowInfo, csInfo } = CommonStore;
  const location = useLocation();
  const navigate = useNavigate();

  const hasCscRights =
    csInfo.csRight && ((csInfo.hasSeat && csInfo.seatStatus === 1) || csInfo.isAdmin);

  const [lgjIsReady, setLgjIsReady] = useState<boolean>(false);
  const [menu, setMenu] = useState<MenuDataItem[]>([]);
  const [userInfo, setUserInfo] = useState<UserInfo>();
  const [teamSelfInfo, setTeamSelfInfo] = useState<Record<string, any>>();

  const [messageOpen, setMessageOpen] = useState<boolean>(false);
  const [messageNum, setMessageNum] = useState<number | null>(null);

  const [faviconUrl, setFaviconUrl] = useState<string>('');
  const [browserTitle, setBrowserTitle] = useState<string>('');

  useFavicon(faviconUrl);
  useTitle(browserTitle);

  // 扁平化菜单，用于搜索，避免频繁计算
  const [flatMenus, setFlatMenus] = useState<Record<string, MenuDataItem> | null>(null);

  const handleHasToken = async () => {
    await AccountStore.getLiguanjiaToken().then((data: any) => {
      setLgjIsReady(true);
      AccountStore.getTeamSelfInfo(data.member_token).then(setTeamSelfInfo);
    });
    AccountStore.getMenuPermission({
      systemId,
      resourceTypeList: [1, 2],
    }).then(async (res) => {
      const __menu = formartMenu(res, [title]);
      setMenu(__menu);

      const __flatMenus = __menu.length ? getFlatMenus(__menu) : null;
      setFlatMenus(__flatMenus);
    });
    AccountStore.getUserInfo().then((data) => {
      // 获取客服权限
      CommonStore.getCsShowInfo(data?.id);
      // CommonStore.getAuthModule({ employeeId: data?.id }).then((data) => setAuthModule(data));
      setUserInfo(data);
      window.localStorage.setItem('userInfo', JSON.stringify(data));
      setFaviconUrl(data.logoImgUrl || ApplicationInfo.textLogo);
      setBrowserTitle(data.portalName || ApplicationInfo.title);
    });
  };

  const [awaitApproveCount, setAwaitApproveCount] = useState<number>(0);
  const getApproveCountNum = async () => {
    const data: any = await CommonStore.tabStatistical({}, systemId);
    setAwaitApproveCount(data.awaitApproveCount);
  };

  // 获取消息数量
  const getMessageCountNum = async () => {
    const param = {
      channelType: 10,
      isRead: -10, // 未读数量
    };
    const data: any = await CommonStore.remindCountNum(param);
    setMessageNum(NP.plus(data?.systemNum, data?.businessNum));
  };

  useLayoutEffect(() => {
    setTheme();

    if (HasToken) {
      handleHasToken();
    } else {
      const loginUrl = `/account/login?redirectUrl=${encodeURIComponent(window.location.href)}`;
      window.location.hostname !== 'localhost' && window.location.replace(loginUrl);
    }
  }, []);

  const [matchMenu, setMatchMenu] = useState<MenuDataItem[]>([]);
  const [currentMenu, setCurrentMenu] = useState<MenuDataItem>();
  useEffect(() => {
    // 切换页面时，重置滚动条
    window.scrollTo(0, 0);

    if (pureRoute.get(location.pathname) || !flatMenus) return;

    const _matchMenu = getMatchMenu(location.pathname, flatMenus, true, true);
    const checkMenu = _matchMenu?.[_matchMenu.length - 1];

    CommonStore.updateState({ currentMenu: checkMenu });
    setMatchMenu(_matchMenu);
    setCurrentMenu({ ...checkMenu });

    if (!checkMenu) {
      navigate('/noAuth', { replace: true });
    }
  }, [flatMenus, location]);

  // 判断用户是否改变
  useEffect(() => {
    getApproveCountNum();
    getMessageCountNum();

    CommonStore.getCsPermissionInfo();

    bus.$on('updateBandgeNum', (types: BandgeFunType[]) => {
      // 重新获取消息数
      const BangdeFunMap: { [key in BandgeFunType]: Function } = {
        approve: getApproveCountNum,
        message: getMessageCountNum,
      };

      types.forEach((type) => BangdeFunMap[type] && BangdeFunMap[type]());
    });

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        AccountStore.checkUserId().then((data: any) => {
          if (!data) {
            window.location.replace('/module/workbench/dashboard');
          }
        });
      } else {
        // 当前标签页变为不可见状态
        // 执行相应的操作
      }
    });
  }, []);

  const logout = () => {
    Promise.allSettled([AccountStore.logout?.(), AccountStore.logoutother?.()]).then(
      async (res) => {
        if (res.filter((item) => item.status === 'fulfilled').length > 0) {
          await Cookies.remove('token');
          window.location.href = loginUrl;
        }
      },
    );
  };

  const showConsoleBtn = useMemo(
    () => flatMenus?.['/group/console'] && flatMenus?.['/group/console']?.children?.length,
    [flatMenus],
  );

  return (
    <>
      <ProLayout
        {...defaultSettings}
        location={location}
        className="peliten__layout"
        logo={ApplicationInfo.textLogo}
        title={ApplicationInfo.title}
        style={{ flexDirection: 'row', height: '100vh' }}
        headerRender={(_, defaultDom) => <div id="fx__layout__header">{defaultDom}</div>}
        headerTitleRender={() => <HeaderLeftContent userInfo={userInfo} />}
        headerContentRender={() => (
          <HeaderMenu
            menuData={menu}
            flatMenus={flatMenus}
            matchMenuKeys={currentMenu?.layout_keys}
          />
        )}
        actionsRender={() => {
          return [
            <Tooltip title="导入导出">
              <TabLink to="/module/common/importAndExport" className="fle-header-action-item">
                <Icon type="icon-icon_20pt_xiazaizhongxin_weixuanzhong" />
              </TabLink>
            </Tooltip>,
            <Tooltip title="审批中心">
              <TabLink to="/module/common/vetting" className="fle-header-action-item">
                <Badge
                  size="small"
                  offset={[8, -5]}
                  count={awaitApproveCount}
                  style={{ top: 5, right: 0 }}
                >
                  <Icon type="icon-icon_16pt_shenpizhongxin_weixuanzhong" />
                </Badge>
              </TabLink>
            </Tooltip>,
            <Tooltip title="消息中心">
              <div
                className="fle-header-action-item fle-header-action-item-div"
                onClick={() => {
                  setMessageOpen(true);
                }}
              >
                <Badge size="small" count={messageNum}>
                  <Icon type="icon-icon_20pt_tongzhi_weixuanzhong" />
                </Badge>
              </div>
            </Tooltip>,
            <div
              style={{
                height: 16,
                width: 1,
                background: '#777',
                padding: 0,
                margin: '0 12px 0 10px',
              }}
            />,
            csShowInfo.show ? (
              <Tooltip title={'咨询平台客服'}>
                <Space
                  align="center"
                  style={{ cursor: 'pointer', marginRight: 10, padding: 0 }}
                  onClick={() => {
                    const params: any = {
                      relationId: userInfo?.id,
                      relationType: RELATION_TYPES.tmallFCT,
                      platform_type: PLATFORM_TYPES.tmallFCT,
                      clientType: '16',
                      csNo: csShowInfo.csNo || '',
                      token: Cookies.get(TokenKey) || '',
                      sourceUrl: encodeURIComponent(window.location.href),
                      sourceType: '分采通会员端',
                    };
                    const paramsStr = Object.keys(params)
                      .map((key) => `${key}=${params[key]}`)
                      .join('&');

                    window.open(
                      `${getApplicationUrl('csc')}/customer/chart?${paramsStr}`,
                      'FCTCscWindow',
                    );
                  }}
                >
                  <Icon
                    type="icon-kefu2"
                    style={{ fontSize: 24, marginTop: 23, color: theme.token?.colorPrimary }}
                  />
                </Space>
              </Tooltip>
            ) : null,
            hasCscRights && (
              <div
                className="user-guidance-item"
                style={{ marginRight: 8 }}
                onClick={() => {
                  const cscUrl = getApplicationUrl('csc');
                  const params: any = {
                    platform_type: PLATFORM_TYPES.carpunion_cs,
                    token: Cookies.get('token') || '',
                    redirectUrl: encodeURIComponent(window.location.origin),
                  };
                  const paramsStr = Object.keys(params)
                    .map((key) => `${key}=${params[key]}`)
                    .join('&');
                  window.open(`${cscUrl}/service/?${paramsStr}`, 'CscWindowShop');
                }}
              >
                <Icon type="icon-kefugongzuotai" />
                <span>客服工作台</span>
              </div>
            ),
            showConsoleBtn && (
              <div
                className="user-guidance-item"
                onClick={() => navigate('/module/workbench/account/carpdanceAccount')}
              >
                <Icon type="icon-yunyingzhongxin" />
                <span>企业控制台</span>
              </div>
            ),
          ];
        }}
        avatarProps={{
          icon: <Icon type="icon-touxiang" style={{ fontSize: 18, color: '#fff' }} />,
          style: { background: theme.token?.colorPrimary },
          title: userInfo?.name,
          render: (_, dom) => (
            <AvatarPopover userInfo={userInfo}>
              <div style={{ whiteSpace: 'nowrap' }}>
                {dom}
                <DownOutlined style={{ fontSize: 12, marginLeft: 10 }} />
              </div>
            </AvatarPopover>
          ),
        }}
        menuRender={() =>
          HasToken && <SiderMenu menuData={menu} flatMenus={flatMenus} currentMenu={currentMenu} />
        }
        footerRender={false}
      >
        <AuthContainer matchMenu={matchMenu} currentMenu={currentMenu} teamSelfInfo={teamSelfInfo}>
          <div className="fx__layout-content">
            <Breadcrumb currentMenu={currentMenu} matchMenu={matchMenu} />
            <TabMenu matchMenu={matchMenu} currentMenu={currentMenu} />
            <Outlet
              context={{
                lgjIsReady,
                userInfo,
                matchMenu,
                currentMenu,
                flatMenus,
              }}
            />
          </div>
        </AuthContainer>
      </ProLayout>

      <Message />

      <NoticeDrawer
        open={messageOpen}
        setMessageOpen={setMessageOpen}
        setMessageNum={setMessageNum}
      />
    </>
  );
};

export default observer(BasicLayout);
